import React from "react";
import { motion } from "framer-motion";
import {
  GraduationCap,
  Award,
  BookOpen,
  Users,
  Building2,
  Utensils,
  Bus,
  HeartPulse,
  Brain,
  Rocket,
  PhoneCall,
  Globe,
  CheckCircle2,
  ShieldCheck,
  ClipboardList,
} from "lucide-react";

// Tailwind palette helpers
const brand = {
  primary: "#20b2aa", // Rankridge teal
  dark: "#0f172a", // slate-900
};

const Badge = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
    {children}
  </span>
);

const Stat = ({ value, label }) => (
  <div className="flex flex-col items-center justify-center rounded-2xl bg-white/80 p-6 shadow-sm ring-1 ring-black/5 backdrop-blur">
    <div className="text-4xl font-extrabold tracking-tight text-slate-900">{value}</div>
    <div className="mt-1 text-sm text-slate-600">{label}</div>
  </div>
);

const Feature = ({ Icon, title, points }) => (
  <div className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
    <div className="flex items-center gap-3">
      <div className="rounded-xl bg-emerald-50 p-2 text-emerald-600 ring-1 ring-emerald-100">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
    </div>
    <ul className="mt-4 space-y-2 text-sm text-slate-600">
      {points.map((p, i) => (
        <li key={i} className="flex gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          <span>{p}</span>
        </li>
      ))}
    </ul>
  </div>
);

const FacilityCard = ({ Icon, title, bullets }) => (
  <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
    <div className="flex items-center gap-3">
      <div className="rounded-xl bg-teal-50 p-2 text-teal-600 ring-1 ring-teal-100">
        <Icon className="h-5 w-5" />
      </div>
      <h4 className="text-base font-semibold text-slate-900">{title}</h4>
    </div>
    <ul className="mt-4 space-y-2 text-sm text-slate-600">
      {bullets.map((b, i) => (
        <li key={i} className="flex gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function RankridgeIITJEE() {
  return (
    <div className="relative isolate overflow-hidden bg-gradient-to-b from-white to-slate-50">
      {/* Decorative background */}
      <div
        className="pointer-events-none absolute inset-0 -z-10 opacity-40 [background:radial-gradient(800px_400px_at_20%_20%,rgba(32,178,170,0.12),transparent_50%),radial-gradient(800px_400px_at_80%_0%,rgba(15,23,42,0.08),transparent_50%)]"
        aria-hidden
      />

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-6 py-2 sm:py-2 lg:py-2">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-7"
          >
            <Badge>
              <ShieldCheck className="h-4 w-4" />
              Hyderabad’s Trusted IIT-JEE Hub
            </Badge>

            <h1 className="mt-5 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Best IIT-JEE Mains & Advanced Long-Term Coaching in Hyderabad
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-700">
              Where Every Dream Gets Direction, Every Student Gets Wings. At
              Rankridge, IIT dreams are not just taught — they are mentored,
              shaped, and fulfilled.
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-3 text-sm text-slate-700 sm:grid-cols-2">
              {[
                "Concept-centric & application-based teaching",
                "1-on-1 doubt clearing & personal mentorship",
                "Weekly booster classes & full-length mocks",
                "AI-based performance analysis",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="tel:+919959271353"
                className="inline-flex items-center gap-2 rounded-2xl bg-[var(--brand)] px-5 py-3 text-base font-semibold text-white shadow-sm transition hover:shadow-md"
                style={{
                  // CSS var for simple theming
                  // @ts-ignore
                  "--brand": brand.primary,
                }}
              >
                <PhoneCall className="h-5 w-5" /> Admissions: +91 99 59 271 353
              </a>
              <a
                href="https://www.rankridge.com/contact-us/"
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 text-base font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                <Globe className="h-5 w-5" /> Enquiry For Admissions
              </a>
            </div>
          </motion.div>

          {/* Right visual */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="lg:col-span-5"
          >
            <div className="relative mx-auto max-w-md">
              <div className="absolute -inset-2 rounded-3xl bg-gradient-to-tr from-emerald-300/40 to-teal-400/30 blur-2xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
                  <span>IIT-JEE Mission Control</span>
                  <span className="inline-flex items-center gap-1"><Rocket className="h-3.5 w-3.5"/>Fuel your dream</span>
                </div>
                <div className="grid gap-4 p-4 sm:grid-cols-2">
                  <Stat value="1400+" label="IIT/NIT selections (5 yrs)" />
                  <Stat value="AIR < 100" label="Consistent top ranks" />
                  <Stat value="35–45" label="Students per batch" />
                  <Stat value="1000+" label="Video solutions" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Rankridge */}
      <section className="mx-auto max-w-7xl px-6 pb-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            What Makes Rankridge the Best?
          </h2>
          <p className="mt-3 text-slate-600">
            Elite faculty, proven results, powerful study material, and a
            classroom culture that nurtures growth.
          </p>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Feature
            Icon={GraduationCap}
            title="India’s Best Faculty"
            points={[
              "IITians, NITians, PhDs & veteran JEE trainers",
              "Concept → Application: the ‘Why’ to ‘How’",
              "Personal mentorship & weekly boosters",
            ]}
          />
          <Feature
            Icon={Award}
            title="Proven Track Record"
            points={[
              "1400+ IIT/NIT selections in 5 years",
              "Consistent AIR under 100 & 500",
              "Success that’s a tradition",
            ]}
          />
          <Feature
            Icon={BookOpen}
            title="Study Material"
            points={[
              "Level-wise JEE Mains & Advanced modules",
              "DPPs, weekly tests & full-length mocks",
              "Rank Booster & Advanced Thinking kits",
            ]}
          />
          <Feature
            Icon={Users}
            title="Nurturing Classrooms"
            points={[
              "Smart AC rooms with digital boards",
              "Batch size 35–45 for focus",
              "Motivational, stress‑free culture",
            ]}
          />
        </div>
      </section>

      {/* Facilities */}
      <section className="mx-auto max-w-7xl px-6 py-2">
        <div className="mx-auto  text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            World‑Class Residential & Semi‑Residential
          </h2>
          <p className="mt-3 text-slate-600">
            Safety, nutrition, discipline and care — so students can focus on
            excellence.
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          <FacilityCard
            Icon={Building2}
            title="Full‑Time Residential"
            bullets={[
              "Separate hostels for boys & girls",
              "Warden supervision & CCTV",
              "24/7 safety & weekly health checks",
            ]}
          />
          <FacilityCard
            Icon={Utensils}
            title="Nutritious 4‑Meal Plan"
            bullets={[
              "Dietician‑curated meals",
              "Protein, grains, greens & dairy",
              "Brain nutrition & energy balance",
            ]}
          />
          <FacilityCard
            Icon={Bus}
            title="Semi‑Residential / Day Scholar"
            bullets={[
              "Extended study hours & evening mentorship",
              "Safe transport for local students",
              "Home support + campus discipline",
            ]}
          />
        </div>
      </section>

      {/* Student Care */}
      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Total Student Care – The Rankridge Culture
            </h2>
            <p className="mt-3 text-slate-600">
              At Rankridge, every student is a responsibility, not a roll
              number.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              {[
                "Academic + emotional counseling",
                "Student app with live performance tracking",
                "Monthly parent meetings & feedback",
                "Yoga & mindfulness to reduce exam stress",
              ].map((item, i) => (
                <li key={i} className="flex gap-2">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-pink-50 p-2 text-pink-600 ring-1 ring-pink-100">
                    <HeartPulse className="h-5 w-5" />
                  </div>
                  <h4 className="font-semibold text-slate-900">Wellness</h4>
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  Regular health check‑ups, wellness monitoring & safe campus.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-indigo-50 p-2 text-indigo-600 ring-1 ring-indigo-100">
                    <Brain className="h-5 w-5" />
                  </div>
                  <h4 className="font-semibold text-slate-900">Mindset</h4>
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  Confidence, resilience and exam temperament built over time.
                </p>
              </div>

 <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-pink-50 p-2 text-pink-600 ring-1 ring-pink-100">
                    <ShieldCheck  className="h-5 w-5" />
                  </div>
                  <h4 className="font-semibold text-slate-900">Safety</h4>
                </div>
                <p className="mt-3 text-sm text-slate-600">
                 Safe Campus with CCTV, GPS-Enabled Buses, and Regular Safety Drills.
                </p>
              </div>
               <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-indigo-50 p-2 text-indigo-600 ring-1 ring-pink-100">
                    <ClipboardList className="h-5 w-5" />
                  </div>
                  <h4 className="font-semibold text-slate-900">Discipline</h4>
                </div>
                <p className="mt-3 text-sm text-slate-600">
                 Structured Routines, Value-Based Education, and Respectful Campus Culture.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="mx-auto max-w-7xl px-6 py-1">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Exclusive Add‑Ons (Only at Rankridge)
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            {
              title: "Olympiads • KVPY • IISER • BITS",
              desc: "Foundation programs for competitive excellence.",
            },
            {
              title: "Digital Learning Portal",
              desc: "1000+ video solutions for doubt resolution.",
            },
            {
              title: "Crash Booster Batches",
              desc: "Mid‑year fast‑track & revision modules.",
            },
            {
              title: "Career Guidance",
              desc: "Roadmaps for IIT, NIT, ISRO & beyond.",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <h4 className="text-base font-semibold text-slate-900">
                {item.title}
              </h4>
              <p className="mt-2 text-sm text-slate-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-20 pt-8">
        <div className="overflow-hidden rounded-3xl border border-teal-200 bg-gradient-to-tr from-teal-50 via-white to-emerald-50 p-8 shadow-sm sm:p-10">
          <div className="grid items-center gap-6 sm:grid-cols-2">
            <div>
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Admissions Open – Limited Seats for 2025
              </h3>
              <p className="mt-2 text-slate-700">
                Join Hyderabad’s No.1 choice for IIT‑JEE coaching. Prepare for
                Mains & Advanced with confidence.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href="tel:+919959271353"
                  className="inline-flex items-center gap-2 rounded-2xl bg-[var(--brand)] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:shadow-md"
                  style={{ "--brand": brand.primary }}
                >
                  <PhoneCall className="h-5 w-5" /> +91 99 59 271 353
                </a>
                <a
                  href="https://www.rankridge.com"
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  <Globe className="h-5 w-5" /> rankridgecollege.com
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-tr from-teal-200/20 to-emerald-200/20 blur-3xl" />
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-teal-100" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      About Rankridge IIT‑JEE
                    </p>
                    <p className="text-xs text-slate-600">
                      Trusted long‑term coaching for top IITs & NITs.
                    </p>
                  </div>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-700">
                  With IITian/NITian faculty, rank‑focused modules, DPPs,
                  full‑length mocks, and advanced booster kits, Rankridge
                  mentors students to excel in both JEE Mains & Advanced — while
                  nurturing confidence, resilience and life skills.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
